import { queryOptions } from '@tanstack/react-query'
import { apiExchange, apiGet, apiPost, apiPostForm, type PostValue } from './client'
import { saveBytes } from './inventoree'

/**
 * Catalogul de servicii - `application/controllers/Service_catalog.php`.
 *
 * Perechile vechi (calea NU poarta `/api/v2`: il pune `apiGet`/`apiPost`):
 *
 *   index()           -> GET  /inventoree/service-catalog
 *   form($id)         -> GET  /inventoree/service-catalog/form/{id}
 *   save()            -> POST /inventoree/service-catalog/save
 *   remove($id)       -> POST /inventoree/service-catalog/{id}/remove
 *   composition($id)  -> GET  /inventoree/service-catalog/{id}/composition
 *   attachable($ci)   -> GET  /inventoree/service-catalog/{id}/attachable
 *   attach()          -> POST /inventoree/service-catalog/{id}/attach
 *   detach($rel)      -> POST /inventoree/service-catalog/detach/{id}
 *   dependencies($id) -> GET  /inventoree/service-catalog/{id}/dependencies
 *   dependable($id)   -> GET  /inventoree/service-catalog/{id}/dependable
 *   addDependency()   -> POST /inventoree/service-catalog/{id}/dependencies
 *   costs($id)        -> GET  /inventoree/service-catalog/{id}/costs
 *   saveCost()        -> POST /inventoree/service-catalog/{id}/costs
 *   removeCost($id)   -> POST /inventoree/service-catalog/costs/{id}/remove
 *   costsDashboard()  -> GET  /inventoree/service-catalog/costs-dashboard
 *
 * ID-URI DE CATALOG, NU DE CI. Trei capete primeau in CI3 un `ci_id` scris in
 * pagina; aici toate primesc id-ul INTRARII, iar serverul il dovedeste al
 * furnizorului inainte de orice citire. Ecranul nu mai poarta `ci_id`-uri prin
 * formulare.
 *
 * FORMA RASPUNSULUI E INREGISTRATA. Fisierele din
 * `src/inventoree/__fixtures__/serviceCatalog*.json` sunt chiar ce raspunde
 * placa; proba de vitest deseneaza ecranele din ele, iar
 * `ServiceCatalogApiTest::testTheAnswerHasExactlyTheShapeTheScreenReads()`
 * verifica pe partea cealalta ca raspunsul viu are inca forma aia. Cusatura e
 * pazita de amandoua capetele, nu de fiecare cu el insusi.
 */

/** `Catalog_services::$scopes` si perechile lui de cost, ca liste. */
export type Choice = { value: number; label: string }

/** Un rand de catalog - aceleasi campuri pe toate ecranele. */
export type CatalogEntry = {
  ID: number
  ci_id: number
  name: string
  short_description: string
  long_description: string
  icon: string
  /** Calea imaginii in bucketul public (`it_service_catalog.image_path`), '' fara imagine. */
  image_path: string
  /** Adresa publica a imaginii - numai pe lista si pe formular. */
  image_url?: string
  /** Rolurile care vad serviciul; gol = toti (cerinta CEC 34). Numai pe lista si pe formular. */
  role_ids?: number[]
  /** Grupurile de tichete (`it_departamente`) care vad serviciul; rol SAU grup. Gol cu role_ids gol = toti. */
  group_ids?: number[]
  published: number
  publish_scope: number
  requires_approval: number
  company_id: number
  company_name: string
  category_id: number
  category_name: string
  criticality: number
  owner_user_id: number
  owner_name: string
  out_relation_count: number
  kind: number
  class_name: string
  request_ticket_type_id: number
  /**
   * Numele tipului de tichet pe care il deschide o cerere - numai in lista.
   * Gol cand nu e tip, sau cand `request_ticket_type_id` arata spre un tip sters.
   */
  ticket_type_name?: string
  commercial_template_id: number
  approver_group_id: number
  status: number
}

/** Un rol al furnizorului (`it_roles`). */
export type CatalogRole = { id: number; name: string }

/** Un grup de tichete al furnizorului (`it_departamente`). */
export type CatalogGroup = { id: number; name: string }

export type CatalogPage = {
  title: string
  entries: CatalogEntry[]
  roles: CatalogRole[]
  groups?: CatalogGroup[]
  companies: { ID: number; name: string }[]
  scopes: Choice[]
  filters: { published: string; kind: number; search: string; ticket_type?: string }
  counts: { services: number; unpublished: number; ownerless: number; empty: number }
  canWrite: boolean
  canDelete: boolean
}

/** `ticketType`: '1' = cu tip de tichet, '0' = fara (sau cu tipul sters), '' = toate. */
export type CatalogFilters = { published: string; kind: number; search: string; ticketType?: string }

export function serviceCatalogQuery(filters: CatalogFilters) {
  const params = new URLSearchParams()
  if (filters.published !== '') params.set('published', filters.published)
  if (filters.kind > 0) params.set('kind', String(filters.kind))
  if (filters.search !== '') params.set('search', filters.search)
  if (filters.ticketType) params.set('ticket_type', filters.ticketType)
  const query = params.toString()

  return queryOptions({
    queryKey: ['service-catalog', 'list', filters],
    queryFn: () => apiGet<CatalogPage>(`/inventoree/service-catalog${query ? `?${query}` : ''}`),
  })
}

/* -------------------------------------------------------------------------
 * formularul
 * ---------------------------------------------------------------------- */

export type CatalogFormPage = {
  entry: CatalogEntry | null
  isEdit: boolean
  services: { ID: number; name: string; company_id: number; company_name: string; kind: number; class_name: string }[]
  categories: { ID: number; name: string }[]
  ticketTypes: { ID: number; type_name: string }[]
  childTicketTypeIds: number[]
  templates: { ID: number; name: string; estimate_unit_cost: number | null; currency: string }[]
  scopes: Choice[]
  /** Rolurile furnizorului, pentru "Visible to". */
  roles: CatalogRole[]
  /** False = migrarea rolurilor n-a rulat: serviciul nu se poate restrange inca. */
  rolesAvailable: boolean
  /** Grupurile de tichete ale furnizorului, pentru "Groups that see it". */
  groups?: CatalogGroup[]
  /** False = migrarea grupurilor n-a rulat. */
  groupsAvailable?: boolean
  /** False = stratul de fisiere (UPDATE_FILES_URL) nu e configurat: nu se urca imagini. */
  imagesAvailable: boolean
  canWrite: boolean
  /** Dreptul `settings.contracts.read` - decide legatura "manage contracts". */
  canManageContracts: boolean
}

export function serviceCatalogFormQuery(id: number) {
  return queryOptions({
    queryKey: ['service-catalog', 'form', id],
    queryFn: () => apiGet<CatalogFormPage>(`/inventoree/service-catalog/form${id > 0 ? `/${id}` : ''}`),
  })
}

/** Raspunsul unei scrieri, in forma pe care o intorcea `jsonResponse()`. */
export type Written = {
  status: 'OK' | 'ERROR'
  message: string
  id?: number
  /** Lantul de dependente, cand salvarea ar inchide un cerc. */
  chain?: string[]
}

export function saveCatalogEntry(fields: Record<string, PostValue>) {
  return apiPost<Written>('/inventoree/service-catalog/save', fields)
}

/** Raspunsul urcarii imaginii. */
export type ImageWritten = Written & { image_path?: string; image_url?: string }

/** Imaginea serviciului (cerinta CEC 33): multipart, campul `image`. */
export function uploadCatalogImage(id: number, file: File) {
  const body = new FormData()
  body.append('image', file)
  return apiPostForm<ImageWritten>(`/inventoree/service-catalog/${id}/image`, body)
}

export function removeCatalogImage(id: number) {
  return apiPost<Written>(`/inventoree/service-catalog/${id}/image/remove`, {})
}

export function removeCatalogEntry(id: number) {
  return apiPost<Written>(`/inventoree/service-catalog/${id}/remove`, {})
}

/* -------------------------------------------------------------------------
 * din ce e facut serviciul
 * ---------------------------------------------------------------------- */

export type CompositionItem = {
  relation_id: number
  origin: number
  ID: number
  name: string
  criticality: number
  last_seen: string
  class_name: string
  kind: number
  relation_name: string
}

export type CompositionPage = {
  entry: CatalogEntry
  composition: CompositionItem[]
  canWrite: boolean
}

export function serviceCatalogCompositionQuery(id: number) {
  return queryOptions({
    queryKey: ['service-catalog', 'composition', id],
    queryFn: () => apiGet<CompositionPage>(`/inventoree/service-catalog/${id}/composition`),
  })
}

export type AttachableItem = { ID: number; name: string; class_name: string; kind: number }

export function serviceCatalogAttachableQuery(id: number, search: string) {
  const query = search === '' ? '' : `?q=${encodeURIComponent(search)}`

  return queryOptions({
    queryKey: ['service-catalog', 'attachable', id, search],
    queryFn: () => apiGet<{ status: string; items: AttachableItem[] }>(
      `/inventoree/service-catalog/${id}/attachable${query}`,
    ),
  })
}

export function attachItem(id: number, targetCiId: number) {
  return apiPost<Written>(`/inventoree/service-catalog/${id}/attach`, { target_ci_id: targetCiId })
}

/** Aceeasi usa pentru compunere si pentru dependenta: amandoua sunt un rand din `it_ci_relations`. */
export function detachRelation(relationId: number) {
  return apiPost<Written>(`/inventoree/service-catalog/detach/${relationId}`, {})
}

/* -------------------------------------------------------------------------
 * de ce alte servicii depinde
 * ---------------------------------------------------------------------- */

export type DependencyRow = {
  relation_id: number
  origin: number
  last_seen: string
  catalog_id: number
  published: number
  publish_scope: number
  ci_id: number
  name: string
  criticality: number
  kind: number
  class_name: string
}

export type DependenciesPage = {
  entry: CatalogEntry
  dependencies: DependencyRow[]
  unpublished: number
  canWrite: boolean
}

export function serviceCatalogDependenciesQuery(id: number) {
  return queryOptions({
    queryKey: ['service-catalog', 'dependencies', id],
    queryFn: () => apiGet<DependenciesPage>(`/inventoree/service-catalog/${id}/dependencies`),
  })
}

export type DependableItem = { catalog_id: number; ci_id: number; name: string; kind: number; class_name: string }

export function serviceCatalogDependableQuery(id: number, search: string) {
  const query = search === '' ? '' : `?q=${encodeURIComponent(search)}`

  return queryOptions({
    queryKey: ['service-catalog', 'dependable', id, search],
    queryFn: () => apiGet<{ status: string; items: DependableItem[] }>(
      `/inventoree/service-catalog/${id}/dependable${query}`,
    ),
  })
}

export function addDependency(id: number, dependsOnCatalogId: number) {
  return apiPost<Written>(`/inventoree/service-catalog/${id}/dependencies`, {
    depends_on_catalog_id: dependsOnCatalogId,
  })
}

/* -------------------------------------------------------------------------
 * costuri
 * ---------------------------------------------------------------------- */

export type CostRule = {
  ID: number
  cost_type: number
  cost_type_name: string
  unit_cost: number
  currency: string
  period: number
  period_name: string
  note: string
}

export type CostsPage = {
  entry: CatalogEntry
  rules: CostRule[]
  costTypes: Choice[]
  periods: Choice[]
  canWrite: boolean
}

export function serviceCatalogCostsQuery(id: number) {
  return queryOptions({
    queryKey: ['service-catalog', 'costs', id],
    queryFn: () => apiGet<CostsPage>(`/inventoree/service-catalog/${id}/costs`),
  })
}

export function saveCostRule(id: number, fields: Record<string, PostValue>) {
  return apiPost<Written>(`/inventoree/service-catalog/${id}/costs`, fields)
}

export function removeCostRule(ruleId: number) {
  return apiPost<Written>(`/inventoree/service-catalog/costs/${ruleId}/remove`, {})
}

/* -------------------------------------------------------------------------
 * tabloul de costuri
 * ---------------------------------------------------------------------- */

/**
 * PE MONEDA, PESTE TOT. Doi clienti facturati in monede diferite n-au un total
 * comun, deci fiecare cifra e o LISTA de perechi moneda-suma, si ecranul le
 * arata alaturi in loc sa le adune.
 */
export type Money = { currency: string; amount: number }

export type CostLine = {
  catalog_id: number
  service_name: string
  company_name: string
  category_name: string
  kind: number
  ci_count: number
  user_count: number
  ticket_count: number
  amount: number
  currency: string
}

export type CostsDashboardPage = {
  periods: number[]
  period: number
  previousPeriod: number
  totals: Money[]
  previous: Money[]
  byCategory: { name: string; amounts: Money[] }[]
  byCompany: { name: string; amounts: Money[] }[]
  top: CostLine[]
  trend: { period_ym: number; amounts: Money[] }[]
  counts: { services: number; items: number; users: number; tickets: number }
}

export function serviceCatalogCostsDashboardQuery(period: number) {
  const query = period > 0 ? `?period_ym=${period}` : ''

  return queryOptions({
    queryKey: ['service-catalog', 'costs-dashboard', period],
    queryFn: () => apiGet<CostsDashboardPage>(`/inventoree/service-catalog/costs-dashboard${query}`),
  })
}

/** `$monthName` din `costs_dashboard.php`: 202608 -> "August 2026". */
export function monthName(ym: number): string {
  if (!ym || ym < 1) return '—'
  const year = Math.trunc(ym / 100)
  const month = ym % 100
  if (month < 1 || month > 12) return String(ym)

  return `${new Date(Date.UTC(year, month - 1, 1)).toLocaleString('en', { month: 'long', timeZone: 'UTC' })} ${year}`
}

/** `$money` din aceeasi macheta: doua zecimale, spatiu la mie. */
export function money(amount: number, currency = ''): string {
  const formatted = amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).replace(/,/g, ' ')

  return currency ? `${formatted} ${currency}` : formatted
}

/* ------------------------------------------------------------------
 * Fotografia de cost si foaia (Settings > Company Reports > Easeedesk Reports)
 *   snapshotForm()  -> GET  /inventoree/service-catalog/snapshot-form?from_ym&to_ym
 *   buildSnapshot() -> POST /inventoree/service-catalog/snapshot
 *   exportCosts()   -> GET  /inventoree/service-catalog/costs/export.xlsx?from_ym&to_ym
 * ---------------------------------------------------------------- */

export interface SnapshotForm {
  fromYm: number
  toYm: number
  firstYear: number
  lastYear: number
  maxMonths: number
}

export function snapshotFormQuery(fromYm: number, toYm: number) {
  return queryOptions({
    queryKey: ['inventoree', 'service-catalog', 'snapshot-form', fromYm, toYm],
    queryFn: () => apiGet<SnapshotForm>(`/inventoree/service-catalog/snapshot-form?from_ym=${fromYm}&to_ym=${toYm}`),
  })
}

export interface SnapshotResult {
  status: 'OK' | 'ERROR'
  message: string
  from_ym?: number
  to_ym?: number
  period?: number
  months?: number
}

export function buildCostSnapshot(fields: { from_month: number; from_year: number; to_month: number; to_year: number }) {
  return apiPost<SnapshotResult>('/inventoree/service-catalog/snapshot', fields)
}

/** Aceeasi foaie ca din catalog: o luna e un interval de o luna. */
export async function downloadCostsXlsx(fromYm: number, toYm: number): Promise<void> {
  const out = await apiExchange(`/inventoree/service-catalog/costs/export.xlsx?from_ym=${fromYm}&to_ym=${toYm}`, { method: 'GET' })
  saveBytes(out.bytes, out.type, out.filename, `service_costs_${fromYm}.xlsx`)
}
